import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import { newsList } from "@/mocks/news";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [copied, setCopied] = useState(false);

  const item = newsList.find((n) => n.id === id);

  if (!item) {
    return (
      <SiteLayout title="资讯未找到 - 明星动态平台">
        <div className="px-4 md:px-8 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-5">
            <i className="ri-article-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-900">
            抱歉，未找到该资讯
          </h1>
          <p className="text-sm text-foreground-500 mt-3 font-body">
            这条资讯可能已被移除，去看看其他最新动态吧。
          </p>
          <Link
            to="/news"
            className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-body hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = newsList
    .filter((n) => n.id !== item.id)
    .sort((a, b) => {
      const sameA = a.category === item.category ? 1 : 0;
      const sameB = b.category === item.category ? 1 : 0;
      if (sameA !== sameB) return sameB - sameA;
      return b.date.localeCompare(a.date);
    })
    .slice(0, 3);

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <SiteLayout
      title={`${item.title} - 白石茉莉奈资讯 - 明星动态平台`}
      description={item.summary}
      keywords={`白石茉莉奈,${item.category},${item.title}`}
    >
      <div className="px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "资讯", to: "/news" },
            { label: item.category, to: `/news?tag=${encodeURIComponent(item.category)}` },
            { label: "正文" },
          ]}
        />
      </div>

      <article className="px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 text-xs font-body px-3 py-1.5 rounded-full bg-accent-500 text-foreground-950">
              <i className="ri-bookmark-line"></i>
              {item.category}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight mt-4">
              {item.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 pb-6 border-b border-background-200 text-sm text-foreground-500 font-body">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {item.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {item.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line"></i>
                {item.views.toLocaleString()} 阅读
              </span>
              <button
                type="button"
                onClick={copyLink}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-link-m"></i>
                复制链接
              </button>
            </div>

            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100 mt-7">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 封面图`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {item.content.map((para, index) => (
                <p
                  key={index}
                  className="text-base text-foreground-700 leading-[1.9] font-body"
                >
                  {para}
                </p>
              ))}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500 font-body">
                所属分类：
              </span>
              <Link
                to={`/news?tag=${encodeURIComponent(item.category)}`}
                className="text-sm font-body px-4 py-2 rounded-full bg-primary-100 text-primary-800 hover:bg-primary-500 hover:text-background-50 transition-colors"
              >
                {item.category}
              </Link>
              <Link
                to="/news"
                className="text-sm font-body px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors"
              >
                返回资讯列表
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <h2 className="font-heading text-lg text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                相关资讯推荐
              </h2>
              <div className="space-y-4">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/news/detail/${rel.id}`}
                    className="group flex gap-3 bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors"
                  >
                    <div className="w-24 h-20 shrink-0 overflow-hidden bg-background-100">
                      <img
                        src={rel.cover}
                        alt={rel.title}
                        title={`${rel.title} - 相关资讯`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="py-3 pr-3">
                      <h3 className="text-sm text-foreground-900 font-body leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <span className="text-xs text-foreground-400 mt-1.5 block font-body">
                        {rel.date}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-14">
          <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-6 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-primary-500"></span>
            更多动态
          </h2>
          <div className="grid grid-cols-1 gap-4 md:gap-5">
            {related.map((rel) => (
              <NewsCard key={rel.id} item={rel} />
            ))}
          </div>
        </section>
      </article>

      {copied && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-body shadow-lg animate-fade-up flex items-center gap-2">
          <i className="ri-check-line text-accent-400"></i>
          链接已复制到剪贴板
        </div>
      )}
    </SiteLayout>
  );
}